import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'hooks/use-redux'
import type { PlaylistId } from 'types/playlist'
import type { State } from 'types/redux'
import { TRACK_NESTING_DND } from '@disco/constants/FeatureTypes'
import { FILE, PLAYLIST, SECTION, TRACK } from '@disco/constants/ItemTypes'
import { SelectionProvider } from 'contexts/selection'
import { ShortlistProvider } from 'contexts/shortlist'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getShortlistItems,
  getShortlistSections,
} from '@disco/state-management/selectors/shortlists'
import {
  getIndexWithoutSections,
  getItemsWithSections,
} from '@disco/utils/shortlist'
import Sortable from 'components/ui/sortable'
import ShortlistSectionItem from './ShortlistSectionItem'
import ShortlistTempItem from './ShortlistTempItem'
import ShortlistTrackItem from './ShortlistTrackItem'
const DROP_TYPES = [FILE, PLAYLIST, SECTION, TRACK] as const
type Props = {
  children: React.ReactNode
  playlistId: PlaylistId | null | undefined
  shortlistId: string
  hidePreview?: boolean
  onOrderUpdate?: (order: string[], item?: any) => void
}

const ShortlistItems = ({
  children,
  playlistId,
  shortlistId,
  onOrderUpdate,
  hidePreview,
}: Props) => {
  const isPhone: boolean = useSelector(isDeviceTypePhone)
  const nestingDndEnabled: boolean = useFeatureEnabled(TRACK_NESTING_DND)
  const items = useSelector((state: State) =>
    getShortlistItems(state, {
      shortlistId,
    }),
  )
  const sections = useSelector((state: State) =>
    getShortlistSections(state, {
      shortlistId,
    }),
  )
  const itemsWithSections = useMemo(
    () => getItemsWithSections(items, sections),
    [items, sections],
  )
  const keyExtractor = useCallback(item => {
    return String(
      // TODO: move experimental `uid` into `id` and simplify keyExtractor
      item.uid ||
        (item.track
          ? item.track.uploadId || item.track.tempId || item.track.id
          : item.id),
    )
  }, [])
  const renderItem = useCallback(
    (item, index, items) => {
      if (!item) {
        return null
      }

      if (item.tempId) {
        return (
          <ShortlistTempItem
            index={item.id}
            itemIndex={getIndexWithoutSections(index, items)}
            sourcePlaylistId={playlistId}
            sourceShortlistId={shortlistId}
            tempId={item.tempId as string}
          />
        )
      } else if (item.track) {
        return (
          <ShortlistTrackItem
            index={item.id}
            uid={item.uid}
            itemIndex={getIndexWithoutSections(index, items)}
            sourcePlaylistId={playlistId}
            sourceShortlistId={shortlistId}
            trackId={item.track.id}
          />
        )
      } else {
        return <ShortlistSectionItem section={item} shortlistId={shortlistId} />
      }
    },
    [playlistId, shortlistId],
  )
  const thresholdHandler = useCallback(
    (
      draggableItem,
      dragOverItem,
      { pointer, entryPoint, targetClientRect },
    ) => {
      const handleFiles =
        (draggableItem?.track || Boolean(draggableItem?.files)) &&
        dragOverItem?.track

      if (handleFiles) {
        const combineThresholdDivisor = 3
        const threshold: number =
          targetClientRect.height / combineThresholdDivisor
        const draggingDown =
          entryPoint &&
          entryPoint.y < pointer.y &&
          pointer.y < targetClientRect.bottom &&
          pointer.y > targetClientRect.bottom - threshold
        const draggingUp =
          entryPoint &&
          entryPoint.y > pointer.y &&
          pointer.y >= targetClientRect.top &&
          pointer.y < targetClientRect.top + threshold
        return !(draggingDown || draggingUp)
      }

      return false
    },
    [],
  )
  return (
    <ShortlistProvider
      value={{
        shortlistId,
      }}
    >
      <SelectionProvider
        value={{
          id: shortlistId,
          data: items,
          paneIndex: null,
        }}
        location="shortlist"
      >
        {isPhone ? (
          itemsWithSections.map(renderItem)
        ) : (
          <Sortable
            accept={DROP_TYPES as any}
            id={`shortlist-${shortlistId}`}
            items={itemsWithSections}
            keyExtractor={keyExtractor}
            renderItem={renderItem}
            onOrderUpdate={onOrderUpdate}
            hidePreview={hidePreview}
            thresholdHandler={nestingDndEnabled ? thresholdHandler : undefined}
          >
            {items => (
              <>
                {items}
                {children}
              </>
            )}
          </Sortable>
        )}
      </SelectionProvider>
    </ShortlistProvider>
  )
}

export default React.memo<Props>(ShortlistItems)
